import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from './AdminLayout';
import { getUserById, updateUser, uploadImage, getFileUrl } from '../../services/apiService';

const AdminProfile = () => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    role: '',
    avatar: null
  });
  const [originalUser, setOriginalUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const userId = localStorage.getItem('userId');
    
    if (!isLoggedIn || !userId) {
      navigate('/login');
      return;
    }
    
    fetchUserProfile(userId);
  }, [navigate]);

  // Load user profile
  const fetchUserProfile = async (userId) => {
    try {
      setIsLoading(true);
      const userData = await getUserById(userId);
      if (userData) {
        setUser({
          name: userData.name || '',
          email: userData.email || '',
          role: userData.role || '',
          avatar: userData.avatar || null
        });
        setOriginalUser(userData);
        if (userData.avatar) {
          setAvatarPreview(getFileUrl(userData.avatar));
        }
        // Update the ID in localStorage if it's a new ObjectId
        if (userData.id && userData.id !== userId) {
          localStorage.setItem('userId', userData.id);
        }
      } else {
        showNotification('User not found. Please log in again.', 'error');
        setTimeout(() => {
          localStorage.clear();
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      showNotification('Error loading profile: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle field changes
  const handleChange = (field, value) => {
    setUser({ ...user, [field]: value });
  };

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image', 'error');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Image must not exceed 5MB', 'error');
      return;
    }

    try {
      setIsUploadingAvatar(true);
      const uploadedFile = await uploadImage(file);
      if (uploadedFile) {
        setUser({ ...user, avatar: uploadedFile.path });
        setAvatarPreview(getFileUrl(uploadedFile.path));
        showNotification('Profile photo updated', 'success');
      }
    } catch (error) {
      showNotification('Error uploading avatar', 'error');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Save changes
  const handleSave = async () => {
    // Validation
    if (!user.name || !user.email) {
      showNotification('Name and email are required', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      showNotification('Invalid email format', 'error');
      return;
    }

    // If email changed, backend can validate uniqueness
    if (originalUser && user.email !== originalUser.email) {
      // We could check if the email already exists here, but we let the backend handle it.
    }

    try {
      setIsSaving(true);
      const userId = localStorage.getItem('userId');
      const updatedUser = await updateUser(userId, {
        name: user.name,
        email: user.email,
        avatar: user.avatar
      });
      
      if (updatedUser) {
        setUser({
          name: updatedUser.name || '',
          email: updatedUser.email || '',
          role: updatedUser.role || '',
          avatar: updatedUser.avatar || null
        });
        setOriginalUser(updatedUser);
        
        // Update localStorage
        localStorage.setItem('userEmail', updatedUser.email);
        localStorage.setItem('userName', updatedUser.name || 'Admin');
        if (updatedUser.id) {
          localStorage.setItem('userId', updatedUser.id);
        }
        
        // Trigger an event to update the header
        window.dispatchEvent(new Event('profileUpdated'));
        
        showNotification('Profile updated successfully!', 'success');
      } else {
        showNotification('Error updating profile', 'error');
      }
    } catch (error) {
      showNotification('Error saving: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset changes
  const handleReset = () => {
    if (originalUser) {
      setUser({
        name: originalUser.name || '',
        email: originalUser.email || '',
        role: originalUser.role || '',
        avatar: originalUser.avatar || null
      });
      if (originalUser.avatar) {
        setAvatarPreview(getFileUrl(originalUser.avatar));
      } else {
        setAvatarPreview(null);
      }
    }
  };

  // Check if changes were made
  const hasChanges = () => {
    if (!originalUser) return false;
    return (
      user.name !== originalUser.name ||
      user.email !== originalUser.email ||
      user.avatar !== originalUser.avatar
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: '#96ca2d' }} />
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Loading profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 25px rgba(150, 202, 45, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: '800',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <i className="fas fa-user-cog" />
            Manage Profile
          </h1>
          <p style={{
            fontSize: '16px',
            margin: '0',
            opacity: 0.9
          }}>
            Update your personal information
          </p>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '300px',
          animation: 'slideInRight 0.3s ease'
        }}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Profile Form */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        {/* Avatar Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '32px',
          paddingBottom: '32px',
          borderBottom: '1px solid #e9ecef'
        }}>
          <div style={{
            position: 'relative',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: avatarPreview ? `url(${avatarPreview})` : 'linear-gradient(135deg, #96ca2d, #7fb069)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
              border: '4px solid white',
              overflow: 'hidden'
            }}>
              {!avatarPreview && (
                <i className="fas fa-user" style={{ fontSize: '48px', color: 'white' }} />
              )}
            </div>
            <label
              htmlFor="avatar-upload"
              style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                background: '#96ca2d',
                color: 'white',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(150, 202, 45, 0.4)',
                transition: 'all 0.3s ease',
                border: '2px solid white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#7fb069';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#96ca2d';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {isUploadingAvatar ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                <i className="fas fa-camera" />
              )}
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
              disabled={isUploadingAvatar}
            />
          </div>
          <p style={{
            margin: '0',
            color: '#6c757d',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            Click the icon to change your profile photo
          </p>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Name Field */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-user" style={{ marginRight: '8px', color: '#96ca2d' }} />
              Full name
            </label>
            <input
              type="text"
              value={user.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Your full name"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#96ca2d';
                e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e9ecef';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Email Field */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-envelope" style={{ marginRight: '8px', color: '#96ca2d' }} />
              Email address
            </label>
            <input
              type="email"
              value={user.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="votre@email.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#96ca2d';
                e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e9ecef';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Role Field (Read-only) */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-user-tag" style={{ marginRight: '8px', color: '#96ca2d' }} />
              Role
            </label>
            <input
              type="text"
              value={user.role}
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '15px',
                background: '#f8f9fa',
                color: '#6c757d',
                cursor: 'not-allowed'
              }}
            />
            <p style={{
              margin: '8px 0 0 0',
              color: '#6c757d',
              fontSize: '12px'
            }}>
              Your role cannot be changed from this page
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '32px',
          paddingTop: '32px',
          borderTop: '1px solid #e9ecef',
          justifyContent: 'flex-end'
        }}>
          {hasChanges() && (
            <button
              onClick={handleReset}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                background: 'white',
                color: '#6c757d',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#dc3545';
                e.currentTarget.style.color = '#dc3545';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              <i className="fas fa-undo" />
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: (isSaving || !hasChanges()) ? '#9ca3af' : 'linear-gradient(135deg, #96ca2d, #7fb069)',
              color: 'white',
              fontWeight: '600',
              cursor: (isSaving || !hasChanges()) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: (isSaving || !hasChanges()) ? 'none' : '0 4px 15px rgba(150, 202, 45, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isSaving && hasChanges()) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(150, 202, 45, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving && hasChanges()) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(150, 202, 45, 0.3)';
              }
            }}
          >
            {isSaving ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save" />
                Save changes
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminProfile;

